import type { KeyboardEvent } from 'react';

import { Box, Theme, useTheme } from '@mui/material';
import ChevronLeft from '@mui/icons-material/ChevronLeft';
import ChevronRight from '@mui/icons-material/ChevronRight';
import { mergeSx } from '@/shared/utils/merge-sx.util';

interface MenuExpandButtonProps {
  expanded: boolean;
  toggleExpanded: () => void;
}

export const MenuExpandButton = ({ expanded, toggleExpanded }: MenuExpandButtonProps) => {
  const theme = useTheme();

  const styles = getStyles(theme);

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      toggleExpanded();
    }
  };

  return (
    <Box
      role="button"
      tabIndex={0}
      aria-label={expanded ? 'Collapse sidebar navigation' : 'Expand sidebar navigation'}
      aria-expanded={expanded}
      onClick={toggleExpanded}
      onKeyDown={handleKeyDown}
      sx={styles.box}
    >
      {expanded ? (
        <ChevronLeft sx={mergeSx(styles.icon, styles.iconLeft)} />
      ) : (
        <ChevronRight sx={mergeSx(styles.icon, styles.iconRight)} />
      )}
    </Box>
  );
};

const getStyles = (theme: Theme) => {
  return {
    box: {
      boxSizing: 'border-box',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      cursor: 'pointer',
      height: '36px',
      width: '36px',
      padding: '8px',
      border: `1px solid ${theme.palette.coolGrey[400]}`,
      borderRadius: '8px',
      backgroundColor: theme.palette.coolGrey[50],
      ':hover': {
        backgroundColor: theme.palette.button.hover,
      },
      ':focus-visible': {
        outline: `2px solid ${theme.palette.primary.main}`,
        outlineOffset: '2px',
      },
      ':active': {
        backgroundColor: theme.palette.button.pressed,
      },
    },
    icon: {
      fontSize: '20px',
    },
    iconRight: {
      color: theme.palette.neutralGrey[500],
    },
    iconLeft: {
      color: theme.palette.neutralGrey[700],
    },
  };
};
